import { useState } from "react";
import { Mail, Phone, MapPin, Calendar, Check, ArrowUpRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import T from "../components/EditableText";
import ConsentCheckbox from "../components/ConsentCheckbox";

export default function Kontakt() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        message: "",
        user_type: "company",
    });
    const [consent, setConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!consent) {
            toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/contact", form);
            setDone(true);
            toast.success("Nachricht gesendet!");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="kontakt-page">
            {/* HERO */}
            <section className="bg-aimploy-bg pt-24 lg:pt-32 pb-12">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="max-w-3xl">
                        <T k="kontakt.hero.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Kontakt</T>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight mt-6">
                            <T k="kontakt.hero.line1">Sprechen</T>
                            <br />
                            <T k="kontakt.hero.line2" className="italic font-medium text-aimploy-violet">wir.</T>
                        </h1>
                        <T k="kontakt.hero.subtitle" as="p" className="text-lg text-aimploy-muted max-w-xl mt-6 leading-relaxed">
                            30 Min. unverbindlich. Wir hören zu, geben Ihnen unsere Einschätzung und schlagen die nächsten Schritte vor.
                        </T>
                    </div>
                </div>
            </section>

            {/* FORM + INFO */}
            <section className="pb-24 lg:pb-32 bg-aimploy-bg">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* FORM */}
                        <div className="lg:col-span-7 bg-white rounded-3xl p-8 lg:p-12 border border-black/5">
                            {done ? (
                                <div className="text-center py-12" data-testid="contact-success">
                                    <div className="h-16 w-16 rounded-full bg-aimploy-violet text-white flex items-center justify-center mx-auto mb-6">
                                        <Check className="w-7 h-7" />
                                    </div>
                                    <h3 className="font-display text-3xl font-bold mb-3">Nachricht ist da.</h3>
                                    <p className="text-aimploy-muted max-w-md mx-auto">
                                        Wir melden uns innerhalb von 24 Stunden zurück.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                                    <h2 className="font-display text-2xl font-bold mb-2">Schreiben Sie uns</h2>

                                    <div>
                                        <label className="text-xs uppercase tracking-[0.2em] text-aimploy-muted font-semibold">Ich bin</label>
                                        <div className="grid grid-cols-2 gap-3 mt-2">
                                            {[
                                                { v: "company", l: "Unternehmen" },
                                                { v: "candidate", l: "Kandidat" },
                                            ].map((o) => (
                                                <button
                                                    key={o.v}
                                                    type="button"
                                                    onClick={() => setForm((f) => ({ ...f, user_type: o.v }))}
                                                    data-testid={`contact-usertype-${o.v}`}
                                                    className={`px-5 py-3 rounded-xl text-sm font-semibold border transition-all ${
                                                        form.user_type === o.v
                                                            ? "bg-aimploy-navy text-white border-aimploy-navy"
                                                            : "bg-aimploy-bg border-aimploy-navy/10 text-aimploy-navy hover:border-aimploy-violet"
                                                    }`}
                                                >
                                                    {o.l}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <input
                                            name="name"
                                            placeholder="Vor- und Nachname"
                                            value={form.name}
                                            onChange={onChange}
                                            required
                                            data-testid="contact-name"
                                            className="border border-aimploy-navy/15 rounded-xl px-5 py-3.5"
                                        />
                                        <input
                                            name="email"
                                            type="email"
                                            placeholder="E-Mail"
                                            value={form.email}
                                            onChange={onChange}
                                            required
                                            data-testid="contact-email"
                                            className="border border-aimploy-navy/15 rounded-xl px-5 py-3.5"
                                        />
                                    </div>

                                    <input
                                        name="company"
                                        placeholder="Unternehmen (optional)"
                                        value={form.company}
                                        onChange={onChange}
                                        data-testid="contact-company"
                                        className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5"
                                    />

                                    <textarea
                                        name="message"
                                        placeholder="Worum geht es?"
                                        value={form.message}
                                        onChange={onChange}
                                        required
                                        rows={6}
                                        data-testid="contact-message"
                                        className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5 resize-none"
                                    />

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="contact-submit"
                                        className="w-full bg-aimploy-navy hover:bg-aimploy-navy-hover text-white px-6 py-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
                                    >
                                        {loading ? "Wird gesendet..." : "Nachricht senden"}
                                        <ArrowUpRight className="w-4 h-4" />
                                    </button>
                                    <ConsentCheckbox checked={consent} onChange={setConsent} testid="contact-consent" />
                                </form>
                            )}
                        </div>

                        {/* INFO + CALENDLY */}
                        <div className="lg:col-span-5 space-y-4">
                            <div className="bg-aimploy-navy text-white rounded-3xl p-8 lg:p-10 relative overflow-hidden">
                                <div className="absolute -bottom-20 -right-20 h-60 w-60 rounded-full bg-aimploy-violet/30 blur-3xl" />
                                <div className="relative">
                                    <h3 className="font-display text-2xl font-bold mb-6"><T k="kontakt.info.title">Direkter Kontakt</T></h3>
                                    <ul className="space-y-5 text-sm">
                                        <li className="flex gap-4">
                                            <Mail className="w-5 h-5 text-aimploy-violet flex-shrink-0 mt-0.5" />
                                            <div>
                                                <T k="kontakt.info.email_label" as="div" className="text-white/60 text-xs uppercase tracking-[0.15em] mb-1">E-Mail</T>
                                                <a href="mailto:kontakt@aimploy.de" className="hover:text-aimploy-violet"><T k="kontakt.info.email" as="span">kontakt@aimploy.de</T></a>
                                            </div>
                                        </li>
                                        <li className="flex gap-4">
                                            <Phone className="w-5 h-5 text-aimploy-violet flex-shrink-0 mt-0.5" />
                                            <div>
                                                <T k="kontakt.info.phone_label" as="div" className="text-white/60 text-xs uppercase tracking-[0.15em] mb-1">Telefon</T>
                                                <a href="tel:+491753669882" className="hover:text-aimploy-violet"><T k="kontakt.info.phone" as="span">+49 175 3669882</T></a>
                                            </div>
                                        </li>
                                        <li className="flex gap-4">
                                            <MapPin className="w-5 h-5 text-aimploy-violet flex-shrink-0 mt-0.5" />
                                            <div>
                                                <T k="kontakt.info.location_label" as="div" className="text-white/60 text-xs uppercase tracking-[0.15em] mb-1">HQ</T>
                                                <T k="kontakt.info.address" as="span">{`Leopoldstraße 2–8\n32051 Herford`}</T>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="bg-white rounded-3xl p-8 lg:p-10 border border-black/5">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="h-10 w-10 rounded-xl bg-aimploy-violet/10 text-aimploy-violet flex items-center justify-center">
                                        <Calendar className="w-5 h-5" />
                                    </div>
                                    <h3 className="font-display text-xl font-bold"><T k="kontakt.cal.title">Termin direkt buchen</T></h3>
                                </div>
                                <T k="kontakt.cal.subtitle" as="p" className="text-sm text-aimploy-muted mb-6">
                                    Wählen Sie einen Slot, der für Sie passt. 30 Min. Video-Call.
                                </T>

                                {/* Booking preview card (Google Calendar Appointments do not allow iframe embedding) */}
                                <div className="bg-gradient-to-br from-aimploy-navy to-aimploy-dark text-white rounded-2xl p-6 lg:p-8 relative overflow-hidden">
                                    <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-aimploy-violet/30 blur-3xl" />
                                    <div className="relative">
                                        {/* Mock calendar visual */}
                                        <div className="flex items-center gap-3 mb-5">
                                            <div className="h-12 w-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                                                <Calendar className="w-6 h-6" />
                                            </div>
                                            <div>
                                                <div className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Erstgespräch</div>
                                                <div className="font-display text-lg font-bold">30 Min. Video-Call</div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-7 gap-1.5 mb-6">
                                            {[...Array(14)].map((_, i) => {
                                                const isAvailable = [2, 5, 6, 9, 11, 12].includes(i);
                                                return (
                                                    <div
                                                        key={i}
                                                        className={`aspect-square rounded-lg flex items-center justify-center text-[10px] font-semibold ${
                                                            isAvailable
                                                                ? "bg-aimploy-violet text-white"
                                                                : "bg-white/5 text-white/30"
                                                        }`}
                                                    >
                                                        {i + 1}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="flex items-center gap-2 text-xs text-white/60 mb-5">
                                            <Check className="w-3.5 h-3.5 text-aimploy-violet" /> Sofort-Bestätigung
                                            <span className="mx-2">·</span>
                                            <Check className="w-3.5 h-3.5 text-aimploy-violet" /> Google Meet inklusive
                                        </div>

                                        <a
                                            href="https://calendar.app.google/81KMC31WttgE7SQb6"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="kontakt-google-calendar-cta"
                                            className="inline-flex items-center justify-center gap-2 w-full bg-white text-aimploy-navy px-6 py-4 rounded-xl text-sm font-bold hover:scale-[1.01] active:scale-[0.99] transition-all"
                                        >
                                            <Calendar className="w-4 h-4" />
                                            <T k="kontakt.cal.cta" as="span">Slot wählen</T>
                                            <ArrowUpRight className="w-4 h-4" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
